import type { ReactNode } from 'react';

export type BundleCardProps = {
    readonly lead: ReactNode;
    readonly bridge?: ReactNode;
    readonly cascade?: ReactNode;
    readonly className?: string;
};

export function BundleCard({ lead, bridge, cascade, className }: BundleCardProps) {
    return (
        <div className={`flex flex-col gap-4 font-sans ${className ?? ''}`}>
            {lead}
            {bridge}
            {cascade ? (
                <div className="animate-fade-in" style={{ animationDuration: '180ms', animationFillMode: 'both' }}>
                    {cascade}
                </div>
            ) : null}
        </div>
    );
}

export type BundleCardSectionProps = {
    readonly label: string;
    readonly status?: ReactNode;
    readonly expanded?: boolean;
    readonly onToggle?: () => void;
    readonly children?: ReactNode;
    readonly className?: string;
};

function BundleCardSection({ label, status, expanded = true, onToggle, children, className }: BundleCardSectionProps) {
    const isClickable = typeof onToggle === 'function';
    const header = (
        <>
            <span className="min-w-0 flex-1 truncate text-left text-xs font-medium leading-tight text-black-600">
                {label}
            </span>
            <span className="ml-auto flex shrink-0 items-center gap-xs pl-s">
                {status}
                {isClickable ? (
                    <span
                        aria-hidden={true}
                        className={`inline-block transition-transform duration-200 ease ${expanded ? 'rotate-180' : ''}`}
                    >
                        ▾
                    </span>
                ) : null}
            </span>
        </>
    );
    return (
        <div
            className={`relative w-full overflow-hidden rounded-xxl border border-gray-300/30 font-sans shadow-sm ${className ?? ''}`}
        >
            {isClickable ? (
                <button
                    type="button"
                    onClick={onToggle}
                    className="flex min-h-9 w-full items-center bg-gray-200 py-1 pl-l pr-m transition-colors duration-200 ease hover:bg-gray-300"
                >
                    {header}
                </button>
            ) : (
                <div className="flex min-h-9 items-center bg-gray-200 py-1 pl-l pr-m">{header}</div>
            )}
            {expanded && children !== undefined ? <div className="bg-white-200 pl-l pr-m py-m">{children}</div> : null}
        </div>
    );
}

export type BundleCardBridgeProps = {
    readonly children: ReactNode;
    readonly className?: string;
};

function BundleCardBridge({ children, className }: BundleCardBridgeProps) {
    return (
        <p
            className={`m-0 self-start rounded-xl bg-white-200 px-l py-m text-xs leading-[1.5] text-black-800 shadow-sm ${className ?? ''}`}
        >
            {children}
        </p>
    );
}

export type BundleCardBridgeSkeletonProps = {
    readonly className?: string;
};

function BundleCardBridgeSkeleton({ className }: BundleCardBridgeSkeletonProps) {
    return (
        <div className={`self-start rounded-xl bg-white-200 px-l py-m shadow-sm ${className ?? ''}`}>
            <div className="flex flex-col gap-xs">
                <div className="h-[10px] w-[180px] animate-pulse rounded-xs bg-gray-200" />
                <div className="h-[10px] w-[140px] animate-pulse rounded-xs bg-gray-200" />
            </div>
        </div>
    );
}

BundleCard.Section = BundleCardSection;
BundleCard.Bridge = BundleCardBridge;
BundleCard.BridgeSkeleton = BundleCardBridgeSkeleton;
